import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from 'antd';
import { LeftArrowWhite } from 'src/assets/images/svg/LeftArrow';
import { Button } from 'src/components/ui/Button';
import { applicationStore } from 'src/store/ApplicationStore';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { workflowStepIterator } from 'src/services/workflowStepIterator';
import { NodeTypes } from 'src/enums/NodeTypes';
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError';

const AddressConfirmation = observer(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        addressLine1: (applicationStore.addressData?.addressLine1 || '').trim(),
        postcode: (applicationStore.addressData?.postcode || '').trim(),
        city: (applicationStore.addressData?.city || '').trim(),
        country: (applicationStore.addressData?.country || '').trim()
    });

    const [errors, setErrors] = useState({
        addressLine1: '',
        postcode: ''
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.addressLine1.trim()) {
            newErrors.addressLine1 = t('addressConfirmation.addressLine1Required');
        }

        if (!formData.postcode.trim()) {
            newErrors.postcode = t('addressConfirmation.postcodeRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const initialAddressLine1 = (applicationStore.addressData?.addressLine1 || '').trim();
            const initialPostcode = (applicationStore.addressData?.postcode || '').trim();

            const trimmedAddressLine1 = formData.addressLine1.trim();
            const trimmedPostcode = formData.postcode.trim();

            const addressLine1Changed = trimmedAddressLine1 !== initialAddressLine1;
            const postcodeChanged = trimmedPostcode !== initialPostcode;

            let body = { "fromGeolocation": true };
            if (addressLine1Changed && postcodeChanged) {
                body = {
                    address_line: trimmedAddressLine1,
                    postcode: trimmedPostcode
                };
            } else if (addressLine1Changed) {
                body = { address_line: trimmedAddressLine1 };
            } else if (postcodeChanged) {
                body = { postcode: trimmedPostcode };
            }

            const response = await workflowStepIterator({
                access_token: applicationStore.access_token,
                node_id: applicationStore.workflowNextServiceResponse.node_id,
                body,
                action_index: 1,
                constextOfStep: {
                    step: NodeTypes.GEOLOCATION_VERIFICATON,
                    retry: applicationStore.workflowNextServiceResponse.retry,
                }
            });

            if (response.data?.service_results?.[0]?.status !== 200) {
                const rawMessage = response.data?.service_results?.[0]?.message;
                const parsedMessage = parseServiceErrorMessage(rawMessage);
                throw new Error(parsedMessage);
            }

            applicationStore.setWorkflowNextServiceResponse(response.data);
            navigate("/next", { replace: true });

        } catch (error) {
            console.log("Error:", error);
            toast.error(error?.message || t('addressConfirmation.errorOccurred'));
            applicationStore.setApplicationErrorObj({
                header: t('addressConfirmation.failed'),
                description: t('addressConfirmation.failedDescription'),
                buttonList: [
                    {
                        label: t('actions.retry'),
                        onClick: () => {
                            navigate("/address-confirmation", { replace: true });
                        },
                        variant: "primaryWithoutIcon"
                    }
                ]
            });
            navigate("/error-page", { replace: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="custom-container flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="form-page-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-4 text-left touch-pan-y">
                    <div className="text-left">
                        <Button
                            className=""
                            variant="primaryBack"
                            onClick={() => {
                                navigate(`/?aid=${applicationStore.aidCode}`, { replace: true });
                            }}>
                            <LeftArrowWhite />
                        </Button>
                    </div>

                    <div className="grid gap-[16px] mt-10">
                        <div className="text-h5 font-bold text-flow-heading">
                            {t('addressConfirmation.title')}
                        </div>
                        <div className="text-body text-Gray900">
                            {t('addressConfirmation.description')}
                        </div>
                    </div>

                    <div className="mt-8 grid gap-6">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-Gray900 text-left">
                                {t('addressConfirmation.addressLine1')}
                            </label>
                            <Input
                                size="large"
                                value={formData.addressLine1}
                                onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                                placeholder={t('addressConfirmation.addressLine1Placeholder')}
                                status={errors.addressLine1 ? 'error' : ''}
                            />
                            {errors.addressLine1 && (
                                <span className="text-sm text-FailRed400">{errors.addressLine1}</span>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-Gray900 text-left">
                                {t('addressConfirmation.postcode')}
                            </label>
                            <Input
                                size="large"
                                value={formData.postcode}
                                onChange={(e) => handleInputChange('postcode', e.target.value)}
                                placeholder={t('addressConfirmation.postcodePlaceholder')}
                                status={errors.postcode ? 'error' : ''}
                            />
                            {errors.postcode && (
                                <span className="text-sm text-FailRed400">{errors.postcode}</span>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-Gray900 text-left">
                                {t('addressConfirmation.city')}
                            </label>
                            <Input
                                size="large"
                                value={formData.city}
                                disabled
                            />
                        </div>

                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-Gray900 text-left">
                                {t('addressConfirmation.country')}
                            </label>
                            <Input
                                size="large"
                                value={formData.country}
                                disabled
                            />
                        </div>
                    </div>
                </div>

                <div className="shrink-0 w-full border-t border-black/[0.08] pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
                    <Button
                        className="w-full"
                        variant="primaryWithoutIcon"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('addressConfirmation.submitting') : t('actions.continue')}
                    </Button>
                </div>
            </div>
        </div>
    );
});

export default AddressConfirmation;
